import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import apiClient from '@/lib/api';
import {
  Link as LinkIcon, ExternalLink, ShieldCheck, User as UserIcon,
  Mail as MailIcon, KeyRound, Loader2, Trash2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SCOPE_LABELS = {
  profile: { icon: UserIcon, label: 'Profile', desc: 'Name, username & picture' },
  email: { icon: MailIcon, label: 'Email address', desc: 'Your primary email' },
  openid: { icon: KeyRound, label: 'Identity', desc: 'A stable account identifier' },
  offline_access: { icon: ShieldCheck, label: 'Offline access', desc: 'Refresh access while away' },
};

const ConnectedAppsPage = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // authorization pending revoke
  const [revoking, setRevoking] = useState(false);

  useEffect(() => { fetchApps(); }, []);

  const fetchApps = async () => {
    try {
      const response = await apiClient.get('/oauth/authorizations');
      setApps(response.data);
    } catch (error) {
      toast.error('Failed to load connected apps');
    } finally {
      setLoading(false);
    }
  };

  const confirmRevoke = async () => {
    if (!target) return;
    setRevoking(true);
    try {
      await apiClient.delete(`/oauth/authorizations/${target.auth_id}`);
      toast.success(`Access revoked for ${target.app?.name || 'app'}`);
      setTarget(null);
      fetchApps();
    } catch (error) {
      toast.error('Failed to revoke access');
    } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-pista animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your approvals…</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="connected-apps-page" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Manage Approvals</h2>
        <p className="text-muted-foreground mt-1">
          Sites and apps you've allowed to sign you in with LostDrkC.
          {apps.length > 0 && <span className="ml-1">{apps.length} connected.</span>}
        </p>
      </div>

      {apps.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <LinkIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No approvals yet</h3>
            <p className="text-muted-foreground">
              When you approve a site with "Login with LostDrkC", it will appear here so you can revoke it any time.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {apps.map((authorization, index) => {
            const app = authorization.app || {};
            return (
              <Card key={authorization.auth_id} data-testid={`connected-app-${index}`} className="border-border/60">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3 min-w-0">
                      <div className="h-12 w-12 rounded-xl bg-pista/15 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {app.logo
                          ? <img src={app.logo} alt={app.name} className="h-full w-full object-cover" />
                          : <ShieldCheck className="h-6 w-6 text-pista" />}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold truncate">{app.name}</h3>
                        {app.website_url && (
                          <a href={app.website_url} target="_blank" rel="noopener noreferrer"
                            className="text-sm text-pista hover:opacity-80 flex items-center gap-1 transition-opacity">
                            <span className="truncate">{(() => { try { return new URL(app.website_url).host; } catch { return 'Visit site'; } })()}</span>
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          </a>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTarget(authorization)}
                      data-testid={`revoke-app-${index}`}
                      className="flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4 mr-1.5 text-destructive" /> Revoke
                    </Button>
                  </div>

                  {app.description && (
                    <p className="text-sm text-muted-foreground mb-4">{app.description}</p>
                  )}

                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Has access to:</p>
                      <ul className="space-y-1.5">
                        {authorization.scopes.map((scope) => {
                          const info = SCOPE_LABELS[scope] || { icon: CheckCircle2, label: scope, desc: '' };
                          const Icon = info.icon;
                          return (
                            <li key={scope} className="flex items-start gap-2 text-sm" data-testid={`app-${index}-scope-${scope}`}>
                              <Icon className="h-4 w-4 text-pista mt-0.5 flex-shrink-0" />
                              <span>{info.label}{info.desc && <span className="text-muted-foreground"> — {info.desc}</span>}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <p className="text-xs text-muted-foreground pt-1 border-t border-border">
                      Approved {format(new Date(authorization.granted_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!target} onOpenChange={(open) => !open && !revoking && setTarget(null)}>
        <AlertDialogContent data-testid="revoke-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access for {target?.app?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This app will lose access to your account immediately and any active sessions it holds will be
              signed out. It will ask for your permission again the next time you log in there.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Keep access</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmRevoke(); }}
              disabled={revoking}
              data-testid="revoke-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revoking ? 'Revoking…' : 'Revoke access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConnectedAppsPage;
